import { useCallback } from 'react';

import {
    useMutation,
    useQueryClient,
    useSuspenseQuery,
    UseSuspenseQueryOptions,
} from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type { AccountNotification, AccountNotificationId, AccountNotificationRequest } from '../generated-types';
import {
    createAccountNotification as createNotification,
    deleteAccountNotification as deleteNotification,
    getAccountNotifications,
    updateAccountNotification as updateNotification,
} from '../services/accountNotificationsService';

const ACCOUNT_NOTIFICATION_KEY = ['account-notifications'];

export const useAccountNotifications = (
    options: Omit<
        UseSuspenseQueryOptions<AccountNotification[], RequestExceptionWithResponse>,
        'queryFn' | 'queryKey'
    > = {},
) =>
    useSuspenseQuery<AccountNotification[], RequestExceptionWithResponse>({
        queryKey: ACCOUNT_NOTIFICATION_KEY,
        queryFn: getAccountNotifications,
        retry: false,
        ...options,
    });

export const useAccountNotificationMutations = () => {
    const queryClient = useQueryClient();

    const invalidateOnSuccess = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: ACCOUNT_NOTIFICATION_KEY });
    }, [queryClient]);

    const createAccountNotification = useMutation<
        AccountNotification,
        RequestExceptionWithResponse,
        AccountNotificationRequest
    >({
        mutationKey: ['create-account-notification'],
        mutationFn: createNotification,
        onSuccess: invalidateOnSuccess,
    });

    const deleteAccountNotification = useMutation<void, RequestExceptionWithResponse, AccountNotificationId>({
        mutationKey: ['delete-account-notification'],
        mutationFn: deleteNotification,
        onSuccess: invalidateOnSuccess,
    });

    const updateAccountNotification = useMutation<
        AccountNotification,
        RequestExceptionWithResponse,
        AccountNotification
    >({
        mutationKey: ['update-account-notification'],
        mutationFn: updateNotification,
        onSuccess: invalidateOnSuccess,
    });

    return {
        createAccountNotification,
        deleteAccountNotification,
        updateAccountNotification,
    };
};
